package com.eladfinish.bible.contest.screens.monopoly.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.IconButton
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.changedToDown
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.correct_answer_bell_gliss_mp3
import com.eladfinish.bible.contest.di.getKoinInstance
import com.eladfinish.bible.contest.dice3droll
import com.eladfinish.bible.contest.extensions.makeBoldByAsterisksAnnotated
import com.eladfinish.bible.contest.extensions.withContinuousUnderlined
import com.eladfinish.bible.contest.fine
import com.eladfinish.bible.contest.ic_light_bulb_on
import com.eladfinish.bible.contest.little_rainy_seasons_correct_mp3
import com.eladfinish.bible.contest.map
import com.eladfinish.bible.contest.pasuk_example
import com.eladfinish.bible.contest.pic
import com.eladfinish.bible.contest.platform.audio.SoundService
import com.eladfinish.bible.contest.platform.haptics.HapticService
import com.eladfinish.bible.contest.prize
import com.eladfinish.bible.contest.question
import com.eladfinish.bible.contest.roll_dice
import com.eladfinish.bible.contest.roll_dice_mp3
import com.eladfinish.bible.contest.screens.monopoly.MonopolyAction
import com.eladfinish.bible.contest.screens.monopoly.MonopolyEffect
import com.eladfinish.bible.contest.screens.monopoly.MonopolyStore
import com.eladfinish.bible.contest.ui.animations.rememberPopInModifier
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.components.ParchmentView
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import com.eladfinish.bible.contest.ui.layout.isShortWideWindow
import com.eladfinish.bible.contest.ui.layout.readableWidth
import com.eladfinish.bible.contest.wrong_answer_wav
import dev.icerock.moko.resources.ImageResource
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import dev.icerock.moko.resources.desc.desc
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.launch
import org.koin.compose.viewmodel.koinViewModel

private const val MAP_IMAGE_URL =
    "https://biblecontest-f3b7c.web.app/images/monopoly-map.png"

private val ColorQuestion = Color(0xFFF59747)
private val ColorPrize = Color(0xFF4B82BF)
private val ColorFine = Color(0xFFBF504D)

@Composable
fun MonopolyScreen(
    showToast: (String) -> Unit,
    snackbarHostState: SnackbarHostState,
    store: MonopolyStore = koinViewModel(),
    soundService: SoundService = getKoinInstance(),
    hapticService: HapticService = getKoinInstance(),
) {
    val state by store.observeState().collectAsState()

    MonopolySideEffectsCollector(
        effects = store.observeSideEffect(),
        soundService = soundService,
        hapticService = hapticService,
        onShowToast = showToast,
    )

    MonopolyScreenContent(
        pasukText = state.pasukText,
        diceImage = state.diceImage,
        isRevealVisible = state.isRevealVisible,
        revealText = state.revealText,
        snackbarHostState = snackbarHostState,
        onQuestionClick = { store.dispatch(MonopolyAction.QuestionClicked) },
        onPrizeClick = { store.dispatch(MonopolyAction.PrizeClicked) },
        onFineClick = { store.dispatch(MonopolyAction.FineClicked) },
        onDiceClick = { store.dispatch(MonopolyAction.DiceClicked) },
    )
}

@Composable
fun MonopolyScreenContent(
    pasukText: String,
    diceImage: ImageResource,
    isRevealVisible: Boolean,
    revealText: String,
    snackbarHostState: SnackbarHostState,
    onQuestionClick: () -> Unit,
    onPrizeClick: () -> Unit,
    onFineClick: () -> Unit,
    onDiceClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val scope = rememberCoroutineScope()

    val map: @Composable (Modifier) -> Unit = { imageModifier ->
        AsyncImage(
            model = ImageRequest.Builder(LocalPlatformContext.current)
                .data(MAP_IMAGE_URL)
                .crossfade(enable = true)
                .build(),
            contentDescription = stringResource(SharedRes.strings.map),
            contentScale = ContentScale.FillWidth,
            alignment = Alignment.TopCenter,
            placeholder = painterResource(SharedRes.images.pic),
            modifier = imageModifier,
        )
    }
    val verse: @Composable ColumnScope.() -> Unit = {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .wrapContentHeight(),
        ) {
            AnimatedParchment(
                pasukText = pasukText,
                modifier = Modifier
                    .align(Alignment.Center)
                    .fillMaxWidth(),
            )
        }

        if (isRevealVisible) {
            IconButton(
                onClick = {},
                modifier = Modifier
                    .align(Alignment.Start)
                    .pointerInput(revealText) {
                        awaitEachGesture {
                            val down = awaitPointerEvent(PointerEventPass.Initial)
                            if (down.changes.none { it.changedToDown() }) return@awaitEachGesture
                            scope.launch {
                                snackbarHostState.currentSnackbarData?.dismiss()
                                snackbarHostState.showSnackbar(message = revealText)
                            }
                            while (true) {
                                val up = awaitPointerEvent(PointerEventPass.Initial)
                                if (up.changes.all { !it.pressed }) {
                                    snackbarHostState.currentSnackbarData?.dismiss()
                                    break
                                }
                            }
                        }
                    },
            ) {
                Image(
                    modifier = Modifier
                        .size(120.dp),
                    painter = painterResource(SharedRes.images.ic_light_bulb_on),
                    contentDescription = null,
                )
            }
        }
    }
    val controls: @Composable () -> Unit = {
        MonopolyCircleButton(
            text = stringResource(SharedRes.strings.question),
            color = ColorQuestion,
            onClick = onQuestionClick,
        )

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 8.dp, bottom = 24.dp),
            contentAlignment = Alignment.Center,
        ) {
            MonopolyCircleButton(
                text = stringResource(SharedRes.strings.fine),
                color = ColorFine,
                onClick = onFineClick,
                modifier = Modifier.align(Alignment.CenterStart),
            )

            Image(
                painter = painterResource(diceImage),
                contentDescription = stringResource(SharedRes.strings.roll_dice),
                modifier = Modifier
                    .size(96.dp)
                    .clickable(onClick = onDiceClick),
            )

            MonopolyCircleButton(
                text = stringResource(SharedRes.strings.prize),
                color = ColorPrize,
                onClick = onPrizeClick,
                modifier = Modifier.align(Alignment.CenterEnd),
            )
        }
    }

    if (isShortWideWindow()) {
        // The map and the verse beside the controls: one above the other they need twice this height.
        Row(
            modifier = modifier
                .fillMaxSize()
                .navigationBarInsetPadding()
                .padding(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                map(Modifier.fillMaxWidth().weight(1f))
                verse()
            }
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                controls()
            }
        }
    } else {
        Column(
            modifier = modifier
                .fillMaxSize()
                .readableWidth()
                .navigationBarInsetPadding()
                .padding(8.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            map(Modifier.fillMaxWidth().weight(1f))
            verse()
            controls()
        }
    }
}

@Composable
private fun MonopolyCircleButton(
    text: String,
    color: Color,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Button(
        onClick = onClick,
        modifier = modifier
            .padding(8.dp)
            .sizeIn(minWidth = 96.dp, minHeight = 96.dp),
        shape = CircleShape,
        contentPadding = PaddingValues(10.dp),
        colors = ButtonDefaults.buttonColors(containerColor = color),
        border = BorderStroke(width = 5.dp, color = Color.White),
    ) {
        Text(
            text = text,
            fontSize = 40.sp,
            textAlign = TextAlign.Center,
            color = Color.White,
        )
    }
}

@Composable
private fun AnimatedParchment(
    pasukText: String,
    modifier: Modifier = Modifier,
) {
    ParchmentView(
        modifier = modifier.then(rememberPopInModifier(key = pasukText.takeIf { it.isNotEmpty() })),
        pasukText = pasukText
            .makeBoldByAsterisksAnnotated()
            .withContinuousUnderlined(),
        onVerseClick = {},
        onVerseLongClick = {},
    )
}

@Composable
private fun MonopolySideEffectsCollector(
    effects: Flow<MonopolyEffect>,
    soundService: SoundService,
    hapticService: HapticService,
    onShowToast: (String) -> Unit,
) {
    val currentOnShowToast by rememberUpdatedState(onShowToast)

    LaunchedEffect(Unit) {
        effects.collect { effect ->
            when (effect) {
                MonopolyEffect.PlayRollSound -> soundService.playIfAllowed(SharedRes.files.roll_dice_mp3)
                MonopolyEffect.PlayQuestionSound -> soundService.playIfAllowed(SharedRes.files.little_rainy_seasons_correct_mp3)
                MonopolyEffect.PlayPrizeSound -> soundService.playIfAllowed(SharedRes.files.correct_answer_bell_gliss_mp3)
                MonopolyEffect.PlayFineSound -> soundService.playIfAllowed(SharedRes.files.wrong_answer_wav)
                MonopolyEffect.VibrateShort -> hapticService.vibrateIfAllowed(milliseconds = 50)
                MonopolyEffect.VibrateMedium -> hapticService.vibrateIfAllowed(milliseconds = 100)
                MonopolyEffect.VibrateLong -> hapticService.vibrateIfAllowed(milliseconds = 200)
                is MonopolyEffect.ShowToast -> currentOnShowToast(effect.message)
                is MonopolyEffect.ShowReveal, MonopolyEffect.HideReveal -> Unit
            }
        }
    }
}

@Preview
@Composable
private fun MonopolyScreenPreview() {
    PreviewTheme {
        MonopolyScreenContent(
            pasukText = SharedRes.strings.pasuk_example.desc().localized(),
            diceImage = SharedRes.images.dice3droll,
            isRevealVisible = true,
            revealText = "תשובה: ירושלים",
            snackbarHostState = remember { SnackbarHostState() },
            onQuestionClick = {},
            onPrizeClick = {},
            onFineClick = {},
            onDiceClick = {},
        )
    }
}
